

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PendingIcon from '@mui/icons-material/Pending';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';


import { PanelCard } from './PanelCard';

import type { StoryboardPanel, StoryboardStrip } from '@/lib/storyboard-types';

interface StripCardProps {
  strip: StoryboardStrip;
  onUpdatePanel: (panelId: string, updates: Partial<StoryboardPanel>) => void;
  onBatchUpdatePanels: (updates: { panelId: string; updates: Partial<StoryboardPanel> }[]) => void;
  onRegeneratePanel: (panelId: string, changeRequest?: string) => void;
  isScrollMode?: boolean;
  // Image generation props
  onGeneratePanelImage?: (panelId: string) => void;
  onRefinePanelImage?: (panelId: string, changeRequest: string) => void;
  onAcceptPanelImage?: (panelId: string, iterationIndex: number) => void;
  isGeneratingImage?: (panelId: string) => boolean;
  isTokenDepleted?: boolean;
}

export function StripCard({
  strip,
  onUpdatePanel,
  onBatchUpdatePanels,
  onRegeneratePanel,
  isScrollMode = false,
  onGeneratePanelImage,
  onRefinePanelImage,
  onAcceptPanelImage,
  isGeneratingImage,
  isTokenDepleted = false,
}: StripCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const acceptedCount = strip.panels.filter((p) => p.scriptStatus === 'accepted').length;
  const totalCount = strip.panels.length;
  const allAccepted = acceptedCount === totalCount;

  const getStatusColor = () => {
    if (allAccepted) return 'success';
    if (acceptedCount > 0) return 'warning';
    return 'default';
  };

  const handleAcceptAll = () => {
    const panelsToUpdate = strip.panels
      .filter((panel) => panel.scriptStatus !== 'accepted')
      .map((panel) => ({
        panelId: panel.id,
        updates: { scriptStatus: 'accepted' as const, changeRequest: undefined },
      }));

    if (panelsToUpdate.length > 0) {
      onBatchUpdatePanels(panelsToUpdate);
    }
  };

  // Vertical scroll mode (webtoon style) - no accordion
  if (isScrollMode) {
    return (
      <Box
        sx={{
          mb: 3,
          borderRadius: 2,
          border: 1,
          borderColor: allAccepted ? 'success.main' : 'divider',
          overflow: 'hidden',
          transition: 'border-color 0.3s ease',
        }}
      >
        {/* Strip Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            bgcolor: allAccepted ? 'success.light' : 'action.hover',
          }}
        >
          {allAccepted ? (
            <CheckCircleIcon color="success" />
          ) : (
            <PendingIcon color="action" />
          )}
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            {t('storyboard.strip_card.strip_number', { order: strip.order })}
          </Typography>
          <Chip
            label={t('storyboard.strip_card.panels_count', { count: acceptedCount, total: totalCount })}
            size="small"
            color={getStatusColor()}
            variant="outlined"
          />
          {!allAccepted && (
            <Button size="small" variant="outlined" onClick={handleAcceptAll}>
                {t('storyboard.strip_card.accept_all')}
            </Button>
          )}
        </Box>

        {/* Vertically stacked panels (webtoon style) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
          }}
        >
          {strip.panels.map((panel) => (
            <Box
              key={panel.id}
              sx={{
                maxWidth: 600,
                mx: 'auto',
                width: '100%',
              }}
            >
              <PanelCard
                panel={panel}
                onUpdate={(updates: Partial<StoryboardPanel>) => onUpdatePanel(panel.id, updates)}
                onRegenerate={(changeRequest?: string) => onRegeneratePanel(panel.id, changeRequest)}
                isGeneratingImage={isGeneratingImage?.(panel.id)}
                onGenerateImage={onGeneratePanelImage ? () => onGeneratePanelImage(panel.id) : undefined}
                onRefineImage={onRefinePanelImage ? (cr: string) => onRefinePanelImage(panel.id, cr) : undefined}
                onAcceptImage={onAcceptPanelImage ? (idx: number) => onAcceptPanelImage(panel.id, idx) : undefined}
                isTokenDepleted={isTokenDepleted}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Default accordion mode with vertical panels
  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      disableGutters
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: '12px !important',
        border: 1,
        borderColor: allAccepted ? 'success.main' : 'divider',
        '&:before': { display: 'none' },
        overflow: 'hidden',
        transition: 'border-color 0.3s ease',
      }}
    >
      <AccordionSummary
        component="div"
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: allAccepted ? 'success.light' : 'action.hover',
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {allAccepted ? (
            <CheckCircleIcon color="success" />
          ) : (
            <PendingIcon color="action" />
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('storyboard.strip_card.strip_number', { order: strip.order })}
          </Typography>
          <Chip
            label={t('storyboard.strip_card.panels_count', { count: acceptedCount, total: totalCount })}
            size="small"
            color={getStatusColor()}
            variant="outlined"
          />
          {!allAccepted && (
            <Chip
              label={t('storyboard.strip_card.accept_all')}
              size="small"
              color="primary"
              clickable
              onClick={(e) => {
                e.stopPropagation();
                handleAcceptAll();
              }}
              sx={{ ml: 'auto', mr: 2 }}
            />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2 }}>
        {/* Vertically stacked panels */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {strip.panels.map((panel) => (
            <Box
              key={panel.id}
              sx={{
                maxWidth: 600,
                mx: 'auto',
                width: '100%',
              }}
            >
            <PanelCard
                panel={panel}
                onUpdate={(updates: Partial<StoryboardPanel>) => onUpdatePanel(panel.id, updates)}
                onRegenerate={(changeRequest?: string) => onRegeneratePanel(panel.id, changeRequest)}
                isGeneratingImage={isGeneratingImage?.(panel.id)}
                onGenerateImage={onGeneratePanelImage ? () => onGeneratePanelImage(panel.id) : undefined}
                onRefineImage={onRefinePanelImage ? (cr: string) => onRefinePanelImage(panel.id, cr) : undefined}
                onAcceptImage={onAcceptPanelImage ? (idx: number) => onAcceptPanelImage(panel.id, idx) : undefined}
                isTokenDepleted={isTokenDepleted}
              />
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
